export const POLICY_CATEGORIES = [
    {
        id: 'motor',
        name: 'Motor Insurance',
        blurb: 'Compulsory and optional cover for vehicles on Gambian roads.',
        products: [
            {
                id: 'tpo',
                name: 'Third-Party Only (TPO)',
                code: 'TPO',
                description: 'The minimum legal requirement for all vehicles on Gambian roads, covering injury or damage to others.'
            },
            {
                id: 'tpft',
                name: 'Third-Party, Fire & Theft (TPFT)',
                code: 'TPFT',
                description: 'Extends basic third-party coverage to protect your own vehicle against fire damage or theft.'
            },
            {
                id: 'comprehensive',
                name: 'Comprehensive Motor',
                code: 'COMP',
                description: 'Covers third-party liabilities alongside accidental damage, vandalism, and medical expenses for your own vehicle.'
            }
        ]
    },
    {
        id: 'life',
        name: 'Life & Savings Policies',
        blurb: 'Protection and targeted savings for families and employers.',
        products: [
            {
                id: 'term_life',
                name: 'Term Life Insurance',
                code: 'TERM',
                description: 'Provides a guaranteed cash payout to beneficiaries if the policyholder passes away within a specific timeframe.'
            },
            {
                id: 'endowment',
                name: 'Endowment Plans',
                code: 'ENDO',
                description: 'Combines life protection with a savings element, paying out a lump sum at maturity or upon death.'
            },
            {
                id: 'group_life',
                name: 'Group Life & Funeral',
                code: 'GLF',
                description: 'Bought by employers for staff benefits, or by individuals to specifically guarantee end-of-life and burial expenses.'
            },
            {
                id: 'education',
                name: 'Education Policies',
                code: 'EDU',
                description: 'Target savings plans designed to fund a child\'s future school or university tuition fees.'
            }
        ]
    },
    {
        id: 'property',
        name: 'Property & Business Asset Insurance',
        blurb: 'Buildings, stock, and portable valuables.',
        products: [
            {
                id: 'fire',
                name: 'Fire & Allied Perils',
                code: 'FIRE',
                description: 'Protects physical structures and inventory against fire, lightning, explosions, and storms.'
            },
            {
                id: 'burglary',
                name: 'Burglary & Housebreaking',
                code: 'BURG',
                description: 'Covers the loss of business stock or personal belongings due to forced, unlawful entry.'
            },
            {
                id: 'all_risks',
                name: 'All Risks Coverage',
                code: 'ARSK',
                description: 'High-tier protection for valuable, portable items (like laptops or jewelry) inside or outside the property.'
            }
        ]
    },
    {
        id: 'medical',
        name: 'Health & Medical Insurance',
        blurb: 'State and private medical cover for residents.',
        products: [
            {
                id: 'nhis',
                name: 'National Health Insurance (NHIS)',
                code: 'NHIS',
                description: 'The state-administered, basic medical safety net for residents in The Gambia.'
            },
            {
                id: 'private_medical',
                name: 'Private Medical Insurance',
                code: 'PMED',
                description: 'Offered by commercial insurers to cover private clinic visits, specialized surgeries, and overseas medical evacuations.'
            }
        ]
    }
];

export function findProduct(categoryId, productId) {
    const category = POLICY_CATEGORIES.find((c) => c.id === categoryId);
    if (!category) return { category: null, product: null };
    return { category, product: category.products.find((p) => p.id === productId) || null };
}

export function calculateGrossPremium(form) {
    const value = parseFloat(form.vehicleValue || form.sumInsured || form.sumAssured || form.targetFund || 0) || 0;
    let nrp = 0;
    if (form.categoryId === 'motor') {
        if (form.productId === 'comprehensive') nrp = 5500 + value * 0.005;
        else if (form.productId === 'tpft') nrp = 4000 + value * 0.0035;
        else nrp = 2500 + value * 0.002;
        if (form.ecowasBrownCard) nrp += 1200;
    } else if (form.categoryId === 'life') {
        if (form.productId === 'term_life') nrp = Math.max(2500, (value / 1000) * 8 * (parseInt(form.termYears, 10) || 10) / 10);
        else if (form.productId === 'endowment') nrp = Math.max(4000, (value / 1000) * 14);
        else if (form.productId === 'group_life') nrp = 800 * Math.max(parseInt(form.livesCovered, 10) || 1, 1);
        else nrp = 3000 + value * 0.04;
    } else if (form.categoryId === 'property') {
        const rate = form.productId === 'all_risks' ? 0.012 : form.productId === 'burglary' ? 0.006 : 0.004;
        nrp = Math.max(form.productId === 'all_risks' ? 3500 : 2000, value * rate);
    } else if (form.categoryId === 'medical') {
        if (form.productId === 'nhis') nrp = 1500;
        else {
            const multiplier = form.ageBracket === 'child' ? 0.8 : form.ageBracket === 'senior' ? 1.6 : 1;
            nrp = (4500 * multiplier) + (parseInt(form.dependentsCount, 10) || 0) * 1500;
            if (form.hasPreExistingConditions) nrp *= 1.35;
        }
    }
    nrp = Math.round(nrp / 100) * 100;
    const wmf = Math.round(nrp * 0.10);
    const graTax = Math.round(nrp * 0.05);
    return { nrp, wmf, graTax, grossPremium: nrp + wmf + graTax };
}

export function normalizePhone(value) {
    return String(value || '').replace(/\D/g, '').replace(/^0+/, '');
}

export function expandGambiaPhone(value) {
    let digits = normalizePhone(value);
    if (digits.startsWith('220')) return digits;
    if (digits.length === 7) digits = '220' + digits;
    if ((digits.length === 8 || digits.length === 9) && digits.startsWith('7')) digits = '220' + digits;
    return digits;
}

export async function hashPin(phone, pin) {
    const data = new TextEncoder().encode(phone + ':' + String(pin || '').trim());
    const buf = await window.crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}
